import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

const PARTY_LABEL = {
  FARMER: "Farmer",
  SUPPLIER: "Supplier",
  BROKER: "Broker",
  DEALER: "Dealer",
  OTHER: "Party",
};

function emandiFormFromApi(data) {
  const bank = data?.party_bank || {};
  const voucher = data?.voucher || {};
  const isFarmer = (data?.party_kind || bank.kind) === "FARMER";
  return {
    party_id: bank.party_id || null,
    party_kind: data?.party_kind || bank.kind || "",
    party_name: bank.display_name || "",
    farmer_bank_name: isFarmer ? bank.bank_name || "" : "",
    farmer_bank_account_no: isFarmer ? bank.bank_account_no || "" : "",
    farmer_bank_ifsc: isFarmer ? bank.bank_ifsc || "" : "",
    party_bank_name: !isFarmer ? bank.bank_name || "" : "",
    party_bank_account_no: !isFarmer ? bank.bank_account_no || "" : "",
    party_bank_ifsc: !isFarmer ? bank.bank_ifsc || "" : "",
    form_6r_no: voucher.form_6r_no || "",
    invoice_no: voucher.invoice_no || "",
    invoice_date: (voucher.invoice_date || "").slice(0, 10),
    form_9r_no: voucher.form_9r_no || "",
    mandi_gate_pass_no: voucher.mandi_gate_pass_no || "",
    pravesh_parchi_no: voucher.pravesh_parchi_no || "",
    erp_gate_pass_code: data?.erp_gate_pass_code || "",
  };
}

export function buildEmandiFinalizePayload(form) {
  const isFarmer = form.party_kind === "FARMER";
  const isSupplier = form.party_kind === "SUPPLIER";
  const bank = isFarmer
    ? {
        bank_name: form.farmer_bank_name?.trim() || undefined,
        bank_account_no: form.farmer_bank_account_no?.trim() || undefined,
        bank_ifsc: form.farmer_bank_ifsc?.trim() || undefined,
      }
    : {
        bank_name: form.party_bank_name?.trim() || undefined,
        bank_account_no: form.party_bank_account_no?.trim() || undefined,
        bank_ifsc: form.party_bank_ifsc?.trim() || undefined,
      };
  const hasBank = Object.values(bank).some(Boolean);

  const voucher = isFarmer
    ? { form_6r_no: form.form_6r_no?.trim() || undefined }
    : {
        ...(isSupplier
          ? {
              invoice_no: form.invoice_no?.trim() || undefined,
              invoice_date: form.invoice_date?.trim()?.slice(0, 10) || undefined,
            }
          : {}),
        form_9r_no: form.form_9r_no?.trim() || undefined,
        mandi_gate_pass_no: form.mandi_gate_pass_no?.trim() || undefined,
        pravesh_parchi_no: form.pravesh_parchi_no?.trim() || undefined,
      };
  const hasVoucher = Object.values(voucher).some(Boolean);

  if (!hasBank && !hasVoucher) return undefined;
  return {
    farmer: isFarmer && hasBank ? bank : undefined,
    party: !isFarmer && hasBank ? bank : undefined,
    voucher: hasVoucher ? voucher : undefined,
  };
}

export default forwardRef(function AccountsEmandiSection({ purchase, form, onPatch, canEdit, onSaved }, ref) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canEdit || !purchase?.id || !form) return;
    setLoading(true);
    api
      .get(`/purchases/${purchase.id}/accounts-emandi`)
      .then((r) => onPatch({ ...emandiFormFromApi(r.data), erp_gate_pass_code: r.data.erp_gate_pass_code || purchase.gate_pass_code || "" }))
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per purchase
  }, [purchase?.id, canEdit]);

  const partyKind = form?.party_kind || purchase?.party_kind || "";

  const save = useCallback(async ({ optional = false } = {}) => {
    if (!purchase?.id || !form) {
      if (optional) return true;
      return false;
    }
    if (!partyKind) {
      if (optional) return true;
      toast.error("Party type not linked on this purchase");
      return false;
    }
    const payload = buildEmandiFinalizePayload(form);
    if (!payload) {
      if (optional) return true;
      toast.error("Enter bank details and/or voucher document numbers to save");
      return false;
    }
    try {
      setSaving(true);
      await api.patch(`/purchases/${purchase.id}/accounts-emandi`, payload);
      toast.success("Bank saved to party · voucher docs saved for this truck");
      if (onSaved) onSaved();
      return true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      setSaving(false);
    }
  }, [form, onSaved, partyKind, purchase?.id]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  if (!canEdit || !purchase || !form) return null;

  const isFarmer = partyKind === "FARMER";
  const isSupplier = partyKind === "SUPPLIER";
  const partyLabel = PARTY_LABEL[partyKind] || "Party";
  const partyId = form.party_id || purchase.party_id;

  const set = (field, value) => onPatch({ [field]: value });

  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/40 p-4 space-y-3" data-testid={`accounts-emandi-${purchase.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-violet-950">
            Payment & e-Mandi — {partyLabel}
            {form.party_name ? ` · ${form.party_name}` : ""}
          </p>
          <p className="text-[10px] text-violet-900/80 mt-1">
            <strong>Bank</strong> is stored on the party profile.{" "}
            <strong>{isFarmer ? "Form 6R" : isSupplier ? "Invoice no. & mandi docs" : "9R / mandi docs"}</strong> apply to this voucher only.
          </p>
        </div>
        {partyId && (
          <Link to={`/parties/${partyId}`} className="text-xs link-brand shrink-0">
            Party profile
          </Link>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : !partyKind ? (
        <p className="text-xs text-amber-800">Link a farmer or supplier party on this purchase first.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
            <fieldset className="space-y-3 rounded border border-emerald-200/80 bg-card/80 p-3">
              <legend className="text-[10px] uppercase tracking-widest text-emerald-800 px-1">Party bank (saved to profile)</legend>
              <Field
                label="Bank name"
                value={isFarmer ? form.farmer_bank_name : form.party_bank_name}
                onChange={(v) => set(isFarmer ? "farmer_bank_name" : "party_bank_name", v)}
                testId="party-bank-name"
              />
              <Field
                label="Account number"
                value={isFarmer ? form.farmer_bank_account_no : form.party_bank_account_no}
                onChange={(v) => set(isFarmer ? "farmer_bank_account_no" : "party_bank_account_no", v)}
                testId="party-bank-account"
              />
              <Field
                label="IFSC"
                value={isFarmer ? form.farmer_bank_ifsc : form.party_bank_ifsc}
                onChange={(v) => set(isFarmer ? "farmer_bank_ifsc" : "party_bank_ifsc", v)}
                testId="party-bank-ifsc"
              />
            </fieldset>

            <fieldset className="space-y-3 rounded border border-violet-200/80 bg-card/80 p-3">
              <legend className="text-[10px] uppercase tracking-widest text-violet-800 px-1">This voucher only</legend>
              {isFarmer ? (
                <Field label="Form 6R no." value={form.form_6r_no} onChange={(v) => set("form_6r_no", v)} testId="form-6r" />
              ) : (
                <>
                  {isSupplier ? (
                    <>
                      <Field label="Invoice no." value={form.invoice_no} onChange={(v) => set("invoice_no", v)} testId="invoice-no" />
                      <div>
                        <Label className="text-[10px] uppercase text-muted-foreground">Invoice date</Label>
                        <Input
                          type="date"
                          className="mt-1 h-8 text-xs"
                          value={form.invoice_date || ""}
                          onChange={(e) => set("invoice_date", e.target.value)}
                          data-testid="invoice-date"
                        />
                      </div>
                    </>
                  ) : null}
                  <Field label="Form 9R no." value={form.form_9r_no} onChange={(v) => set("form_9r_no", v)} testId="form-9r" />
                  <Field
                    label="e-Mandi gate pass no."
                    value={form.mandi_gate_pass_no}
                    onChange={(v) => set("mandi_gate_pass_no", v)}
                    testId="mandi-gate-pass"
                    hint="Not the ERP gate pass"
                  />
                  <Field label="Pravesh parchi no." value={form.pravesh_parchi_no} onChange={(v) => set("pravesh_parchi_no", v)} testId="pravesh-parchi" />
                </>
              )}
              <div className="pt-1 border-t border-border/60">
                <Label className="text-[10px] uppercase text-muted-foreground">ERP gate pass (reference)</Label>
                <p className="text-xs font-mono mt-1 text-muted-foreground">{form.erp_gate_pass_code || purchase.gate_pass_code || "—"}</p>
              </div>
            </fieldset>
          </div>
        </>
      )}
    </div>
  );
});

function Field({ label, value, onChange, testId, hint }) {
  return (
    <div>
      <Label className="text-[10px] uppercase text-muted-foreground">{label}</Label>
      <Input className="mt-1 h-8 text-xs" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}
